import { faDollarSign } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import MaskedInput from 'react-text-mask';
import styled from 'styled-components';
import createNumberMask from 'text-mask-addons/dist/createNumberMask';
import { ConditionalRendering } from '../conditional-rendering';
import Tooltip from '../tooltip/Tooltip';
import ArrowButton, {ARROWDIRECTION} from '../button/StepArrowButtons';

const StyledIcon = styled.span`
    /* Prefer top/bottom stretch over percentage height — more reliable across browsers
       when the relatively positioned parent sizes from its input child. */
    position: absolute;
    top: 1px;
    left: 1px;
    bottom: 1px;
    width: 25px;
    box-sizing: border-box;
    border-radius: 4px 0 0 4px;
    align-items: center;
    justify-content: center;
    display: flex;
    flex-wrap: nowrap;
    z-index: 1;
    margin: 0;
    background-color: ${({ theme }) => theme.gray9};
    color: ${({ theme }) => theme.gray3};
    border: ${({ theme }) => `1px solid ${theme.gray7}`};
`;

const StyledCurrencyInputContainer = styled.div`
    min-width: 10rem;
    position: relative;
    input {
        padding-left: ${(props) => props.inputPaddingLeft || '25px'};
        max-width: ${(props) => props.maxWidth || (props.hasLargeInput ? '11rem' : '9rem')} !important;
    }
    .arrow-container{
        margin-left: -15px;
    }
`;

const StyledCurrencyInput = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: stretch;
    position: relative;
`;

const defaultMaskOptions = {
    prefix: '$',
    suffix: '',
    includeThousandsSeparator: true,
    thousandsSeparatorSymbol: ',',
    allowDecimal: true,
    decimalSymbol: '.',
    decimalLimit: 2, // how many digits allowed after the decimal
    integerLimit: 6, // limit length of integer numbers
    allowNegative: false,
    allowLeadingZeroes: false,
};

const CurrencyInput = props => {
    const {
        maskOptions = {},
        inputMode = 'numeric',
        label: { wrapperClasses, labelClasses, showRequired, labelText, ariaLabel },
        input,
        showIcon,
        hasLargeInput=false,
        isStepArrowsVisible=false,
        preserveDollarOnBlur=false,
        showTooltip=true,
        inputPaddingLeft,
        maxWidth,
        input: { id, name, value, inputclasses, fieldArrayIndex = undefined, fieldArray = [], isDisabled = false },
        actions: { handleChange, handleBlur = () => {}, handleStepChange = () => {} },
    } = props;

    const currencyMask = createNumberMask({
        ...defaultMaskOptions,
        ...maskOptions,
    });

    const handleValueChange = e => {
        handleChange({ target: { name, value: e.target.value } }, fieldArray, fieldArrayIndex);
    };

    const handleValueBlur = e => {
        const rawValue = e.target.value;

        // Convert trailing "." or ".0"/".00" to fixed 2-decimal format
        if (rawValue && (rawValue.endsWith('.') || rawValue.endsWith('.0') || rawValue.endsWith('.00'))) {
            const parsed = parseFloat(rawValue.replace(/[^0-9.]/g, '')); // remove $ or commas
            if (!isNaN(parsed)) {
                e.target.value = preserveDollarOnBlur ? `$${parsed.toFixed(2)}` : parsed.toFixed(2);
                handleChange({ target: { name, value: e.target.value } }, fieldArray, fieldArrayIndex);
            }
        }

        handleBlur(e);
    };

    return (
        <Fragment>
            <StyledCurrencyInputContainer
                className={`grid-row gi-input gi-component ${wrapperClasses && wrapperClasses.join(' ')}`}
                hasLargeInput={hasLargeInput}
                inputPaddingLeft={inputPaddingLeft}
                maxWidth={maxWidth}
            >
                <ConditionalRendering isTrue={labelText}>
                    <label
                        htmlFor={name}
                        className={`gi-input__label ${labelClasses && labelClasses.join(' ')} required-${showRequired}`}
                        aria-label={ariaLabel}
                    >
                        <FormattedMessage id={labelText} />
                        <ConditionalRendering isTrue={showTooltip}>
                            <Tooltip
                                id="anonymous.pre_eligibility.pre_screen.content.income.field1.1"
                                className="tooltip__display--inline"
                                content="anonymous.pre_eligibility.pre_screen.content.income.field1.1.tooltip"
                                index="1"
                                place="top"
                            />
                        </ConditionalRendering>
                    </label>
                </ConditionalRendering>

                <StyledCurrencyInput>
                    <ConditionalRendering isTrue={showIcon}>
                        <StyledIcon>
                            <FontAwesomeIcon icon={faDollarSign} className="gi-input__icon" />
                        </StyledIcon>
                    </ConditionalRendering>

                    <MaskedInput
                        id={id}
                        data-testid={id}
                        data-automation-id={id}
                        name={name}
                        className={`gi-input__input ${inputclasses && inputclasses.join(' ')}`}
                        mask={currencyMask}
                        {...input}
                        value={value}
                        onChange={handleValueChange}
                        onBlur={handleValueBlur}
                        disabled={isDisabled}
                    />
                </StyledCurrencyInput>
                <ConditionalRendering isTrue={isStepArrowsVisible}>
                    <div className='arrow-container'>
                        <ArrowButton direction={ARROWDIRECTION.UP} onClick={()=>handleStepChange(ARROWDIRECTION.UP)} />
                        <ArrowButton direction={ARROWDIRECTION.DOWN} onClick={()=>handleStepChange(ARROWDIRECTION.DOWN)} />
                    </div>
                </ConditionalRendering>
            </StyledCurrencyInputContainer>
        </Fragment>
    );
};

CurrencyInput.propTypes = {
    inputmode: PropTypes.string,
    preserveDollarOnBlur: PropTypes.bool,
    inputPaddingLeft: PropTypes.string,
    maxWidth: PropTypes.string,
    maskOptions: PropTypes.shape({
        prefix: PropTypes.string,
        suffix: PropTypes.string,
        includeThousandsSeparator: PropTypes.bool,
        thousandsSeparatorSymbol: PropTypes.string,
        allowDecimal: PropTypes.bool,
        decimalSymbol: PropTypes.string,
        decimalLimit: PropTypes.string,
        requireDecimal: PropTypes.bool,
        allowNegative: PropTypes.bool,
        allowLeadingZeroes: PropTypes.bool,
        integerLimit: PropTypes.number,
    }),
};

export default CurrencyInput;
